import React, { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiBookmark,
  FiChevronDown,
  FiChevronRight,
  FiCopy,
  FiExternalLink,
  FiGitBranch,
  FiMap,
  FiMessageCircle,
  FiMoreHorizontal,
  FiSearch,
  FiShare2,
  FiTarget,
} from "react-icons/fi";
import { GET_ALL_GISTS, GET_ALL_SUBJECTS } from "../../services/graphql/queriesMutations";
import styles from "./GistsCanonicalPage.module.css";

type SubjectItem = {
  id: number;
  title: string;
  parentId: number | null;
};

type GistComment = {
  id: number;
  comment?: string | null;
  parentId?: number | null;
  isRemoved?: boolean | null;
  createdAt?: string | null;
  user?: {
    id?: number | null;
    name?: string | null;
  } | null;
};

type GistEdit = {
  id: number;
  body?: string | null;
  createdAt?: string | null;
  newnessCount?: number | null;
  importantCount?: number | null;
  qualityCount?: number | null;
  comments?: GistComment[] | null;
  user?: {
    id?: number | null;
    name?: string | null;
    image?: string | null;
  } | null;
};

type GistVersion = {
  id: number;
  point?: string | null;
  createdAt?: string | null;
  edits?: GistEdit[] | null;
  user?: {
    id?: number | null;
    name?: string | null;
    image?: string | null;
  } | null;
};

type GistItem = {
  id: number;
  title: string;
  parentId?: number | null;
  subjectId?: number | null;
  status?: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  versions?: GistVersion[] | null;
};

type SubjectsQuery = {
  subjects: SubjectItem[];
};

type GistsQuery = {
  gists: GistItem[];
};

const EMPTY_SUBJECTS: SubjectItem[] = [];
const EMPTY_GISTS: GistItem[] = [];

type GistTreeNode = {
  gist: GistItem;
  depth: number;
  ordinal: string;
};

function stripHtml(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function formatDate(value?: string | null): string {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function getLatestVersion(gist: GistItem): GistVersion | null {
  const versions = gist.versions || [];
  if (!versions.length) return null;
  return versions.slice().sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0];
}

function getLatestEdit(gist: GistItem): GistEdit | null {
  const edits = (gist.versions || []).flatMap((version) => version.edits || []);
  if (!edits.length) return null;
  return edits.slice().sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0];
}

function getAuthorDetails(gist: GistItem) {
  const latestEdit = getLatestEdit(gist);
  const latestVersion = getLatestVersion(gist);
  return {
    name: latestEdit?.user?.name || latestVersion?.user?.name || "Gistoscope author",
    image: latestEdit?.user?.image || latestVersion?.user?.image || "/profile.png",
    date: latestEdit?.createdAt || latestVersion?.createdAt || gist.publishedAt || gist.createdAt,
  };
}

function getSummaryText(gist: GistItem): string {
  const point = stripHtml(getLatestVersion(gist)?.point);
  if (point) return point;
  const body = stripHtml(getLatestEdit(gist)?.body);
  if (body) return body.length > 220 ? `${body.slice(0, 220).trim()}...` : body;
  return "This gist is ready to read once its body is added.";
}

function getBodyParagraphs(gist: GistItem): string[] {
  const body = stripHtml(getLatestEdit(gist)?.body) || getSummaryText(gist);
  if (!body) return ["This gist does not have readable body text yet."];
  const naturalBreaks = body
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (naturalBreaks.length > 1) return naturalBreaks.slice(0, 8);
  const sentences = body.split(/(?<=[.!?])\s+/).map((part) => part.trim()).filter(Boolean);
  if (sentences.length <= 3) return [body];
  const paragraphs: string[] = [];
  for (let index = 0; index < sentences.length; index += 2) {
    paragraphs.push(sentences.slice(index, index + 2).join(" "));
    if (paragraphs.length >= 6) break;
  }
  return paragraphs;
}

function getComments(gist: GistItem): GistComment[] {
  const seen = new Set<number>();
  return (gist.versions || [])
    .flatMap((version) => version.edits || [])
    .flatMap((edit) => edit.comments || [])
    .filter((comment) => {
      if (!comment || comment.isRemoved || !stripHtml(comment.comment)) return false;
      if (seen.has(comment.id)) return false;
      seen.add(comment.id);
      return true;
    })
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
}

function countVotes(gist: GistItem): number {
  return (gist.versions || [])
    .flatMap((version) => version.edits || [])
    .reduce((total, edit) => total + (edit.newnessCount || 0) + (edit.importantCount || 0) + (edit.qualityCount || 0), 0);
}

function getSubjectPath(subjects: SubjectItem[], subjectId: number | null): SubjectItem[] {
  if (subjectId === null) return [];
  const byId = subjects.reduce<Record<number, SubjectItem>>((acc, subject) => {
    acc[subject.id] = subject;
    return acc;
  }, {});
  const path: SubjectItem[] = [];
  const visited = new Set<number>();
  let current: SubjectItem | undefined = byId[subjectId];
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    path.unshift(current);
    current = typeof current.parentId === "number" ? byId[current.parentId] : undefined;
  }
  return path;
}

function getDescendantSubjectIds(subjectId: number, subjects: SubjectItem[]): number[] {
  const childrenByParent = new Map<number, SubjectItem[]>();
  subjects.forEach((subject) => {
    if (typeof subject.parentId !== "number") return;
    const current = childrenByParent.get(subject.parentId) || [];
    current.push(subject);
    childrenByParent.set(subject.parentId, current);
  });
  const result: number[] = [];
  const visit = (id: number) => {
    (childrenByParent.get(id) || []).forEach((child) => {
      result.push(child.id);
      visit(child.id);
    });
  };
  visit(subjectId);
  return result;
}

function buildTreeNodes(gists: GistItem[]): GistTreeNode[] {
  const byParent = new Map<number | "root", GistItem[]>();
  const ids = new Set(gists.map((gist) => gist.id));
  gists.forEach((gist) => {
    const parentKey = typeof gist.parentId === "number" && ids.has(gist.parentId) ? gist.parentId : "root";
    const current = byParent.get(parentKey) || [];
    current.push(gist);
    byParent.set(parentKey, current);
  });
  byParent.forEach((items, key) => {
    byParent.set(
      key,
      items.slice().sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || "") || a.title.localeCompare(b.title)),
    );
  });

  const rows: GistTreeNode[] = [];
  const visit = (gist: GistItem, depth: number, ordinal: string) => {
    rows.push({ gist, depth, ordinal });
    (byParent.get(gist.id) || []).forEach((child, index) => visit(child, depth + 1, `${ordinal}.${index + 1}`));
  };
  (byParent.get("root") || []).forEach((gist, index) => visit(gist, 0, String(index + 1)));
  return rows;
}

function getParentGist(gist: GistItem, gists: GistItem[]): GistItem | null {
  if (typeof gist.parentId !== "number") return null;
  return gists.find((candidate) => candidate.id === gist.parentId) || null;
}

function getRelatedGists(gist: GistItem, gists: GistItem[]): GistItem[] {
  const siblings = gists.filter((candidate) => candidate.parentId === gist.parentId && candidate.id !== gist.id);
  const children = gists.filter((candidate) => candidate.parentId === gist.id);
  return [...children, ...siblings].slice(0, 4);
}

const GistsCanonicalPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const subjectIdFromUrl = Number(searchParams.get("subjectId"));
  const gistIdFromUrl = Number(searchParams.get("gistId"));
  const { data: subjectsData, loading: subjectsLoading, error: subjectsError } = useQuery<SubjectsQuery>(GET_ALL_SUBJECTS);
  const { data: gistsData, loading: gistsLoading, error: gistsError } = useQuery<GistsQuery>(GET_ALL_GISTS);
  const subjects = subjectsData?.subjects ?? EMPTY_SUBJECTS;
  const gists = gistsData?.gists ?? EMPTY_GISTS;

  const subjectOptions = useMemo(() => {
    return subjects
      .map((subject) => ({
        subject,
        count: gists.filter((gist) => gist.subjectId === subject.id).length,
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count || a.subject.title.localeCompare(b.subject.title));
  }, [gists, subjects]);

  const selectedSubjectId = useMemo(() => {
    if (Number.isFinite(subjectIdFromUrl) && subjects.some((subject) => subject.id === subjectIdFromUrl)) return subjectIdFromUrl;
    const news = subjectOptions.find((item) => item.subject.title.toLowerCase() === "news");
    return (news || subjectOptions[0])?.subject.id || subjects[0]?.id || null;
  }, [subjectIdFromUrl, subjectOptions, subjects]);

  const branchGists = useMemo(() => {
    if (selectedSubjectId === null) return gists;
    const branchIds = new Set([selectedSubjectId, ...getDescendantSubjectIds(selectedSubjectId, subjects)]);
    const result = gists.filter((gist) => typeof gist.subjectId === "number" && branchIds.has(gist.subjectId));
    return result.length ? result : gists;
  }, [gists, selectedSubjectId, subjects]);

  const treeNodes = useMemo(() => buildTreeNodes(branchGists), [branchGists]);

  const selectedGist = useMemo(() => {
    if (Number.isFinite(gistIdFromUrl)) {
      const fromUrl = branchGists.find((gist) => gist.id === gistIdFromUrl) || gists.find((gist) => gist.id === gistIdFromUrl);
      if (fromUrl) return fromUrl;
    }
    return treeNodes[0]?.gist || branchGists[0] || null;
  }, [branchGists, gistIdFromUrl, gists, treeNodes]);

  useEffect(() => {
    if (!selectedGist || selectedSubjectId === null) return;
    if (Number(searchParams.get("subjectId")) === selectedSubjectId && Number(searchParams.get("gistId")) === selectedGist.id) return;
    const next = new URLSearchParams(searchParams);
    next.set("subjectId", String(selectedSubjectId));
    next.set("gistId", String(selectedGist.id));
    setSearchParams(next, { replace: true });
  }, [searchParams, selectedGist, selectedSubjectId, setSearchParams]);

  const selectedSubject = subjects.find((subject) => subject.id === selectedSubjectId) || null;
  const subjectPath = getSubjectPath(subjects, selectedSubjectId);
  const selectedTreeNode = treeNodes.find((node) => node.gist.id === selectedGist?.id);
  const parentGist = selectedGist ? getParentGist(selectedGist, branchGists) : null;
  const comments = selectedGist ? getComments(selectedGist) : [];
  const relatedGists = selectedGist ? getRelatedGists(selectedGist, branchGists) : [];
  const author = selectedGist ? getAuthorDetails(selectedGist) : null;
  const bodyParagraphs = selectedGist ? getBodyParagraphs(selectedGist) : [];
  const isLoading = subjectsLoading || gistsLoading;
  const hasError = subjectsError || gistsError;

  const chooseGist = (gist: GistItem) => {
    const next = new URLSearchParams(searchParams);
    if (typeof gist.subjectId === "number") next.set("subjectId", String(gist.subjectId));
    next.set("gistId", String(gist.id));
    setSearchParams(next);
  };

  const chooseSubject = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSubjectId = Number(event.target.value);
    const branchIds = new Set([nextSubjectId, ...getDescendantSubjectIds(nextSubjectId, subjects)]);
    const firstGist = gists.find((gist) => typeof gist.subjectId === "number" && branchIds.has(gist.subjectId));
    const next = new URLSearchParams(searchParams);
    next.set("subjectId", String(nextSubjectId));
    if (firstGist) next.set("gistId", String(firstGist.id));
    else next.delete("gistId");
    setSearchParams(next);
  };

  if (isLoading) {
    return (
      <main className={styles.pageShell}>
        <div className={styles.stateCard}>Loading the canonical Gists viewer...</div>
      </main>
    );
  }

  if (hasError) {
    return (
      <main className={styles.pageShell}>
        <div className={styles.stateCard}>Could not load gists. Check that the local backend is running and the tunnel is open.</div>
      </main>
    );
  }

  if (!selectedGist || !selectedSubject) {
    return (
      <main className={styles.pageShell}>
        <div className={styles.stateCard}>No gists are available yet. Create a gist first, then return to this viewer.</div>
      </main>
    );
  }

  return (
    <main className={styles.pageShell}>
      <section className={styles.topModeBar} aria-label="Gists viewer mode">
        <div className={styles.modeBrand}>
          <span className={styles.modeIcon}><FiGitBranch /></span>
          <span>
            <strong>Styles / Gists</strong>
            <small>Canonical reader</small>
          </span>
        </div>
        <div className={styles.modeTabs}>
          <span className={styles.modeTabActive}>Read</span>
          <span>Discussion</span>
          <span>Related</span>
          <span>Versions</span>
        </div>
        <div className={styles.searchBox}>
          <FiSearch />
          <span>Search Gistoscope</span>
          <kbd>⌘ K</kbd>
        </div>
      </section>

      <section className={styles.viewerGrid}>
        <aside className={styles.leftRail}>
          <div className={styles.railLabel}>Subject</div>
          <label className={styles.subjectPickerCard}>
            <span className={styles.subjectIcon}>▤</span>
            <span className={styles.subjectPickerCopy}>
              <span>{selectedSubject.title}</span>
              <small>{branchGists.length} connected {branchGists.length === 1 ? "gist" : "gists"}</small>
            </span>
            <select value={selectedSubject.id} onChange={chooseSubject} aria-label="Choose subject">
              {subjectOptions.map(({ subject, count }) => (
                <option key={subject.id} value={subject.id}>{subject.title} ({count})</option>
              ))}
            </select>
            <FiChevronDown className={styles.subjectPickerChevron} />
          </label>

          <div className={styles.breadcrumbs}>
            {subjectPath.slice(-3).map((subject, index) => (
              <React.Fragment key={subject.id}>
                {index > 0 && <span>›</span>}
                <span>{subject.title}</span>
              </React.Fragment>
            ))}
            <span>›</span>
            <strong>Gists</strong>
          </div>

          <div className={styles.mapHeader}>
            <h2>Discussion map</h2>
            <span>Click a gist</span>
          </div>

          <div className={styles.treeList}>
            {treeNodes.map((node) => {
              const isSelected = node.gist.id === selectedGist.id;
              const commentCount = getComments(node.gist).length;
              return (
                <button
                  key={node.gist.id}
                  type="button"
                  className={`${styles.treeNode} ${isSelected ? styles.treeNodeSelected : ""}`}
                  style={{ "--depth": node.depth } as React.CSSProperties}
                  onClick={() => chooseGist(node.gist)}
                >
                  <span className={styles.treeConnector} />
                  <span className={styles.treeOrdinal}>{node.ordinal}</span>
                  <span className={styles.treeTitle}>{node.gist.title}</span>
                  {commentCount > 0 && <span className={styles.treeMeta}>{commentCount}</span>}
                </button>
              );
            })}
          </div>

          <Link to="/navigator" className={styles.openMapButton}>
            <FiMap />
            Open full map
          </Link>
        </aside>

        <article className={styles.readerPane}>
          <header className={styles.readerHeader}>
            <div className={styles.readerKicker}>
              <span>GIST</span>
              <strong>{selectedTreeNode?.ordinal || selectedGist.id}</strong>
            </div>
            <div className={styles.readerTitleRow}>
              <h1>{selectedGist.title}</h1>
              <div className={styles.readerTools} aria-label="Reader tools">
                <button type="button"><FiBookmark /></button>
                <button type="button"><FiExternalLink /></button>
                <button type="button"><FiMoreHorizontal /></button>
              </div>
            </div>
            <div className={styles.readerMeta}>
              <img src={author?.image || "/profile.png"} alt="" />
              <strong>{author?.name}</strong>
              <span>•</span>
              <span>{formatDate(author?.date)}</span>
              <span>•</span>
              <span>{Math.max(1, Math.ceil(stripHtml(getLatestEdit(selectedGist)?.body).split(/\s+/).length / 180))} min read</span>
              <span>•</span>
              <span>🔥 {countVotes(selectedGist)}</span>
              <span>•</span>
              <span>💬 {comments.length}</span>
            </div>
          </header>

          <section className={styles.keyPointBox}>
            <span className={styles.spark}>✦</span>
            <div>
              <strong>Key point</strong>
              <p>{getSummaryText(selectedGist)}</p>
            </div>
          </section>

          <section className={styles.parentCard}>
            <FiChevronRight />
            <span>Parent gist:</span>
            <strong>{parentGist?.title || "This is a root gist in the selected discussion branch"}</strong>
            {parentGist && <button type="button" onClick={() => chooseGist(parentGist)}>View</button>}
          </section>

          <div className={styles.bodyLayout}>
            <div className={styles.anchorRail} aria-hidden="true">
              {bodyParagraphs.slice(0, 4).map((_, index) => (
                <span key={index}>{index + 1}</span>
              ))}
            </div>
            <div className={styles.bodyText}>
              {bodyParagraphs.map((paragraph, index) => (
                <p key={`${selectedGist.id}-${index}`}>{paragraph}</p>
              ))}
            </div>
          </div>

          <footer className={styles.readerFooter}>
            <button type="button"><FiBookmark /> Bookmark</button>
            <button type="button"><FiMessageCircle /> Cite gist</button>
            <button type="button"><FiShare2 /> Share</button>
            <button type="button"><FiCopy /> Copy link</button>
            <button type="button" className={styles.focusButton}><FiTarget /> Focus mode</button>
          </footer>
        </article>

        <aside className={styles.rightRail}>
          <div className={styles.discussionHeader}>
            <h2>Discussion</h2>
            <FiMoreHorizontal />
          </div>
          <div className={styles.commentTabs}>
            <button type="button" className={styles.commentTabActive}>Anchored comments</button>
            <button type="button">All comments <span>{comments.length}</span></button>
          </div>

          <div className={styles.commentList}>
            {comments.slice(0, 3).map((comment, index) => (
              <article key={comment.id} className={styles.commentCard}>
                <div className={styles.commentAnchor}>Attached to paragraph {Math.min(index + 1, Math.max(bodyParagraphs.length, 1))}<span>{index + 1}</span></div>
                <div className={styles.commentAuthorRow}>
                  <span className={styles.commentAvatar}>{(comment.user?.name || "G").slice(0, 1).toUpperCase()}</span>
                  <strong>{comment.user?.name || "Gistoscope user"}</strong>
                  <small>{formatDate(comment.createdAt)}</small>
                </div>
                <p>{stripHtml(comment.comment).slice(0, 210)}</p>
                <div className={styles.commentActions}>♡ {index + 5}<span>Reply</span><FiMoreHorizontal /></div>
              </article>
            ))}
            {!comments.length && (
              <article className={styles.emptyDiscussionCard}>
                No comments yet. This panel is ready for anchored comments once this gist receives discussion.
              </article>
            )}
          </div>

          <div className={styles.relatedHeader}>
            <h3>Related gists</h3>
            <span>View all</span>
          </div>
          <div className={styles.relatedList}>
            {relatedGists.map((gist) => (
              <button key={gist.id} type="button" onClick={() => chooseGist(gist)} className={styles.relatedItem}>
                <span>•</span>
                <strong>{gist.title}</strong>
                <small>{getAuthorDetails(gist).name} · {formatDate(getAuthorDetails(gist).date)}</small>
              </button>
            ))}
            {!relatedGists.length && <div className={styles.emptyRelated}>No related gists in this branch yet.</div>}
          </div>
        </aside>
      </section>
    </main>
  );
};

export default GistsCanonicalPage;
